import { useEffect, useRef, useState } from "react";
import { SharingApi } from "../api";
import { SharedListItem, SharedProjectResponse, TodoData } from "../../../types";
import { OPEN_SHARED_EVENT } from "./InviteGate";
import { createSharedSteps } from "../providers/sharedSteps";
import { createRenameGuard } from "../providers/renameGuard";

// "Shared with me": the caller's shares (max 25, with project titles when the API can resolve them), and
// a view of one project where a sharee can add steps, check them off and
// rename them (nothing else). Loaded on open / on click only; there is no
// polling and nothing here depends on its own results.
export const SharedWithMe = () => {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState<SharedListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<SharedProjectResponse | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [steps, setSteps] = useState<TodoData[]>([]);
  const [writable, setWritable] = useState(true);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);

  // Only the latest openProject call may update the view.
  const requestId = useRef(0);
  // Mirrors `steps` so async handlers always read the current list.
  const stepsRef = useRef<TodoData[]>([]);
  const actions = useRef<ReturnType<typeof createSharedSteps> | null>(null);
  // Settles each edit once (Escape or commit) so the blur from the unmounting
  // input neither saves a cancelled edit nor commits a second time.
  const renameGuard = useRef(createRenameGuard());

  const loadShares = async () => {
    setError(null);
    try {
      setShares((await SharingApi.listShares()).shares);
    } catch {
      setError("Couldn't load shared projects.");
    }
  };

  const openProject = async (parentId: string) => {
    const id = ++requestId.current;
    setOpen(true);
    setProject(null);
    setProjectError(null);
    setWriteError(null);
    setWritable(true);
    setEditing(null);
    setNewTitle("");
    actions.current = null;
    try {
      const result = await SharingApi.getShared(parentId);
      if (id === requestId.current) {
        stepsRef.current = result.steps;
        setSteps(result.steps);
        actions.current = createSharedSteps({
          api: SharingApi,
          projectId: parentId,
          projectCreatedAt: result.parent.createdAt,
          context: result.parent.context,
          // A write that settles after another project was opened is dropped.
          mutate: (fn) => {
            if (id !== requestId.current) return;
            stepsRef.current = fn(stepsRef.current);
            setSteps(stepsRef.current);
          },
          getSteps: () => stepsRef.current,
          onError: (m) => id === requestId.current && setWriteError(m),
          onBlocked: (m) => {
            if (id !== requestId.current) return;
            setWritable(false);
            setWriteError(m);
          },
        });
        setProject(result);
      }
    } catch {
      if (id === requestId.current) {
        setProjectError("Couldn't load this project. It may no longer be shared.");
      }
    }
  };

  // An accepted invite jumps straight into the project (and refreshes the list).
  useEffect(() => {
    const onOpen = (e: Event) => {
      loadShares();
      openProject((e as CustomEvent<string>).detail);
    };
    window.addEventListener(OPEN_SHARED_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SHARED_EVENT, onOpen);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && shares === null) loadShares();
  };

  const sortedSteps = [...steps].sort((a, b) => a.order - b.order);

  const submitNew = () => {
    const title = newTitle.trim();
    if (!title) return;
    setNewTitle("");
    actions.current?.addStep(title);
  };

  const commitRename = () => {
    if (!editing || !renameGuard.current.shouldCommit()) return;
    renameGuard.current.committed();
    const step = stepsRef.current.find((x) => x.id === editing.id);
    setEditing(null);
    if (step) actions.current?.rename(step, editing.title);
  };

  return (
    <section>
      <button onClick={toggle} className="text-sm font-semibold text-neutral-500">
        <span className="text-xs">{open ? "▾" : "▸"}</span> Shared with me
        {shares ? ` (${shares.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {shares?.length === 0 && (
            <p className="text-sm text-neutral-400">Nothing has been shared with you.</p>
          )}
          {shares && shares.length > 0 && (
            <ul className="space-y-1">
              {shares.map((s) => (
                <li key={`${s.ownerId}_${s.parentId}`} className="bg-white border rounded">
                  <button
                    onClick={() => openProject(s.parentId)}
                    className="w-full text-left px-3 py-2 text-sm"
                  >
                    {s.title ? (
                      <span className="block truncate">{s.title}</span>
                    ) : (
                      <>Shared project · since {new Date(s.createdAt).toLocaleDateString()}</>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {projectError && <p className="text-sm text-red-600">{projectError}</p>}
          {project && (
            <div className="border rounded bg-white px-3 py-2">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold">{project.parent.title}</h3>
                {!writable && <span className="text-xs text-neutral-400">read-only</span>}
              </div>
              {project.parent.notes && (
                <p className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">
                  {project.parent.notes}
                </p>
              )}
              {writeError && (
                <div className="mt-2 px-2 py-1 rounded text-xs flex justify-between bg-red-50 text-red-800">
                  <span>{writeError}</span>
                  <button onClick={() => setWriteError(null)} className="ml-3">
                    dismiss
                  </button>
                </div>
              )}
              {sortedSteps.length === 0 ? (
                <p className="text-sm text-neutral-400 mt-2">No steps.</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {sortedSteps.map((t) => {
                    const saving = !t.createdAt; // still being created
                    return (
                      <li
                        key={t.id}
                        className={`text-sm flex items-center gap-2 ${
                          t.done ? "line-through text-neutral-400" : ""
                        }`}
                      >
                        {writable ? (
                          <input
                            type="checkbox"
                            checked={t.done}
                            disabled={saving}
                            aria-label={`Mark "${t.title}" ${t.done ? "not done" : "done"}`}
                            onChange={(e) => actions.current?.setDone(t, e.target.checked)}
                          />
                        ) : (
                          <span>{t.done ? "✓" : "○"}</span>
                        )}
                        {writable && editing?.id === t.id ? (
                          <input
                            autoFocus
                            value={editing.title}
                            maxLength={500}
                            onChange={(e) => setEditing({ id: t.id, title: e.target.value })}
                            onBlur={commitRename}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename();
                              if (e.key === "Escape") {
                                renameGuard.current.cancel();
                                setEditing(null);
                              }
                            }}
                            className="flex-1 border rounded px-1 text-sm text-neutral-900"
                          />
                        ) : (
                          <span className="flex-1">{t.title}</span>
                        )}
                        {writable && editing?.id !== t.id && (
                          <button
                            disabled={saving}
                            onClick={() => {
                              renameGuard.current.start();
                              setEditing({ id: t.id, title: t.title });
                            }}
                            className="text-xs text-neutral-400 disabled:opacity-50"
                          >
                            rename
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {writable && (
                <form
                  className="mt-2 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitNew();
                  }}
                >
                  <input
                    value={newTitle}
                    maxLength={500}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Add a step"
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  />
                  <button type="submit" disabled={!newTitle.trim()} className="text-sm disabled:opacity-50">
                    Add
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
